/**
 * @file OpenTelemetry HTTP Success Response Handler
 * @description Handles successful HTTP responses by logging, adding OpenTelemetry events,
 * setting span attributes, and recording metrics.
 */

import { logAndTraceErrorResponse } from './logAndTraceErrorResponse';
import { SpanStatusCode } from '@opentelemetry/api';
import { EVENTS, SEVERITY_TEXT, STAGES } from '../../constants';
import { log } from '../../logging';
import { getDynamicHttpLogAttributes } from '../../utils/getDynamicHttpLogAttributes';
import { SEMATTRS_HTTP_STATUS_CODE } from '@opentelemetry/semantic-conventions';
import { HTTP_STATUS_TEXT } from '../../constants/attributeNames';
import { withSafelyExecute } from '../../utils/withSafelyExecute';
import { calculateSizeInBytes, getDuration, getIdsFromSpan } from '../../utils';
import { otlHandleHttpResponseMetrics } from '../../utils/otlHandleHttpResponseMetrics';

/**
 * Handles a successful HTTP response, adding OpenTelemetry events, logging, and recording metrics.
 *
 * @param {Object} response - The Axios response object.
 * @param {Object} response.config - The Axios request configuration.
 * @param {Object} response.config.span - The OpenTelemetry span associated with the request.
 * @param {Object} response.config.context - The OpenTelemetry context associated with the request.
 * @param {Object} [response.config.logData] - Additional data to include in the log.
 * @param {string} response.config.url - The URL of the request.
 * @param {string} response.config.method - The HTTP method (GET, POST, etc.).
 * @param {number} response.status - The HTTP status code.
 * @param {string} response.statusText - The HTTP status text.
 * @param {Object} response.data - The response data.
 * @returns {Object} The response object.
 */
function otlHandleSuccessResponse(response) {
    const { span, logData, url, method } = response.config || {};
    const { status, statusText, data } = response || {};
    if (!span) {
        return;
    }

    // Handle error case where response has status 200 but indicates a failure
    if (data.status === 0) {
        logAndTraceErrorResponse({
            span,
            url,
            logData,
            statusText,
            status,
            error: JSON.stringify(data.data),
        });
        otlHandleHttpResponseMetrics({ method, url, duration: getDuration(span), status });

        return;
    }
    const dataSizeInBytes = calculateSizeInBytes(data);
    // Add OpenTelemetry event for response received
    span.addEvent(EVENTS.RESPONSE_RECEIVED);
    // Log the successful response
    log(`Request to ${url} succeeded`, SEVERITY_TEXT.INFO, {
        url,
        status,
        ...getDynamicHttpLogAttributes({ url, stage: STAGES.RESPONSE, data, dataSizeInBytes }),
        ...getIdsFromSpan(span),
        ...logData?.attributes,
    });
    // Set OpenTelemetry span attributes and status
    span.setAttributes({
        [SEMATTRS_HTTP_STATUS_CODE]: status,
        [HTTP_STATUS_TEXT]: statusText,
    });
    span.setStatus({ code: SpanStatusCode.OK });
    span.end();
    // Record HTTP response metrics
    otlHandleHttpResponseMetrics({ method, url, duration: getDuration(span), status, dataSizeInBytes });
}

export default withSafelyExecute(otlHandleSuccessResponse);
